/**
 * Copyright (c) 2023 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import { Grid } from '@mui/material';
import { useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { Sanitize } from 'src/DistributionPackages/BestFit.Magicline/Resources/Private/App/Components/Sanitizer';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

/**
 * SummaryForm Component
 * @param fourthStepContent
 * @returns {Element}
 * @constructor
 */
export default function SummaryForm({ fourthStepContent }) {
  const { values } = useFormikContext();
  const { t } = useTranslation();
  const clientsTimeZone = dayjs.tz.guess();

  /**
     * Get human-readable due date
     * @returns {string}
     */
  const getHumReadableDateTime = () => {
    const dueDate = values.dueDate.split('+');
    return t('fromToTime', {
      startTime: dayjs(dueDate[0])
        .tz(clientsTimeZone)
        .format('HH:mm'),
      endTime: dayjs(dueDate[1])
        .tz(clientsTimeZone)
        .format('HH:mm'),
    });
  };

  /**
     * Get human-readable due date
     * @returns {string}
     */
  const getHumReadableDate = () => {
    const dueDate = values.dueDate.split('+');
    return dayjs(dueDate[0])
      .tz(clientsTimeZone)
      .format('DD.MM.YYYY');
  };

  return (
    <>
      <Sanitize value={fourthStepContent} />
      <Grid container spacing={2}>
        <Grid item xs={6}>
          {t('firstname')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.firstName}
        </Grid>
        <Grid item xs={6}>
          {t('surname')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.lastName}
        </Grid>
        <Grid item xs={6}>
          {t('birthday')}
          :
        </Grid>
        <Grid item xs={6}>
          {dayjs(values.dateOfBirth)
            .format('DD.MM.YYYY')}
        </Grid>
        <Grid item xs={6}>
          {t('mail')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.email}
        </Grid>
        <Grid item xs={6}>
          {t('phone')}
          :
        </Grid>
        <Grid item xs={6}>
          {`${values.phonePrefix} ${values.phone}`}
        </Grid>
        <Grid item xs={6}>
          {t('date')}
          :
        </Grid>
        <Grid item xs={6}>
          {getHumReadableDate()}
        </Grid>
        <Grid item xs={6}>
          {t('time')}
          :
        </Grid>
        <Grid item xs={6}>
          {getHumReadableDateTime()}
        </Grid>
        <Grid item xs={6}>
          {t('trainingFormat')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.formOfTraining === 'true' ? t('withTrainer') : t('withoutTrainer')}
        </Grid>
        <Grid item xs={6}>
          {t('selection')}
          :
        </Grid>
        <Grid item xs={6}>
          {values.studioName}
        </Grid>
      </Grid>
    </>
  );
}

SummaryForm.propTypes = {
  fourthStepContent: PropTypes.object.isRequired,
};
